import React, { useState } from 'react';
import { Input, Switch } from '@rebass/forms';
import classNames from 'classnames';
import { Button } from 'rebass';

import NiceModal from 'components/NiceModal';
import { useForm } from 'hooks/useForm';
import { useKeyValidator } from 'hooks/useKeyValidator';
import { useOnPasteBlocker } from 'hooks/useOnPasteBlocker';
import { useAccountPanelistsCountQuery } from 'api/useAccountPanelistsCountQuery';
import { useToastHelper } from 'hooks/useToastHelper';
import { useEnableDoubleOptInMutation } from 'api/useEnableDoubleOptInMutation';
import { useDisableDoubleOptInMutation } from 'api/useDisableDoubleOptInMutation';
import services from 'services/services';
import { useAuth } from 'context/AuthContext';

const CONFIRMATION_PHRASE = 'enable double opt-in';

/**
 * @typedef {React.InputHTMLAttributes<HTMLInputElement>} DoubleOptInSwitchProps
 */

/**
 * Enable/disable double opt-in switch.
 *
 * @param {DoubleOptInSwitchProps} props
 */
export function DoubleOptInSwitch({ className, ...rest }) {
    const auth = useAuth();
    const { addErrorToast, addSuccessToast } = useToastHelper();
    const { isLoading, data } = useAccountPanelistsCountQuery();
    const { register, handleSubmit, reset, watch } = useForm();
    const watchedConfirmation = watch('confirmation');
    const isConfirmationValid = useKeyValidator(watchedConfirmation, CONFIRMATION_PHRASE);
    const blockOnPaste = useOnPasteBlocker();

    const { mutate: enableDoubleOptIn, isLoading: isEnabling } = useEnableDoubleOptInMutation({
        onSuccess: data => {
            addSuccessToast(data.message);
            auth.account.settings.is_double_opt_in_enabled = true;
            closeEnablingDialog();
        },
        onError: error => {
            const errorText = services.parseAndTrackXhrErrors(error) || 'Something went wrong. Please try again later.';
            addErrorToast(errorText);
        }
    });
    const { mutate: disableDoubleOptIn, isLoading: isDisabling } = useDisableDoubleOptInMutation({
        onSuccess: data => {
            addSuccessToast(data.message);
            auth.account.settings.is_double_opt_in_enabled = false;
            closeDisablingDialog();
        },
        onError: error => {
            const errorText = services.parseAndTrackXhrErrors(error) || 'Something went wrong. Please try again later.';
            addErrorToast(errorText);
        }
    });
    const [isEnablingDialogOpen, setIsEnablingDialogOpen] = useState(false);
    const [isDisablingDialogOpen, setIsDisablingDialogOpen] = useState(false);
    const isDoubleOptInEnabled = !!auth?.account?.settings?.is_double_opt_in_enabled;

    const closeEnablingDialog = () => {
        reset();
        setIsEnablingDialogOpen(false);
    };

    const closeDisablingDialog = () => {
        setIsDisablingDialogOpen(false);
    };

    const onEnableSubmit = handleSubmit(() => {
        enableDoubleOptIn();
    });

    return (
        <>
            <Switch
                className={classNames('switch-small', className)}
                disabled={isLoading}
                checked={isDoubleOptInEnabled}
                onClick={() => (isDoubleOptInEnabled ? setIsDisablingDialogOpen(true) : setIsEnablingDialogOpen(true))}
                {...rest}
            />
            <NiceModal
                title="Enable Double Opt-In"
                isOpen={isEnablingDialogOpen}
                shouldCloseOnOverlayClick
                onRequestClose={closeEnablingDialog}
            >
                <p className="color-text-secondary mb-6">
                    By taking this action, all <b>existing</b> panelists who match <b>Opt In = True</b> will be
                    automatically be marked <b>Opt In Confirmed = True</b> (aka Double Opt In). Going forward, all{' '}
                    <b>new</b> panelists will need to verify their email address before joining your panel.
                    <br />
                    <a
                        href="https://help.dscout.com/private-panels/panel-management/require-double-opt-in"
                        target="_blank"
                    >
                        More information
                    </a>
                </p>
                <form id="enable-double-opt-in-form" onSubmit={onEnableSubmit}>
                    <p className="mb-3">To continue, type "{CONFIRMATION_PHRASE}":</p>
                    <Input
                        {...register('confirmation')}
                        onPaste={blockOnPaste}
                        autoFocus
                        autoComplete="off"
                        placeholder={CONFIRMATION_PHRASE}
                    />
                </form>
                <div className="modal-actions">
                    <Button type="button" mr={2} onClick={closeEnablingDialog} variant="secondary-gray">
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        mr={0}
                        className="modal-primary"
                        form="enable-double-opt-in-form"
                        disabled={!isConfirmationValid || isEnabling}
                    >
                        Enable Double Opt-In
                    </Button>
                </div>
            </NiceModal>
            <NiceModal
                title="Disable Double Opt-In"
                isOpen={isDisablingDialogOpen}
                shouldCloseOnOverlayClick
                onRequestClose={closeDisablingDialog}
            >
                <p className="color-text-secondary mb-6">Are you sure you want to disable double opt-in?</p>
                <div className="modal-actions">
                    <Button type="button" mr={2} onClick={closeDisablingDialog} variant="secondary-gray">
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        mr={0}
                        className="modal-primary"
                        onClick={disableDoubleOptIn}
                        disabled={isDisabling}
                    >
                        Disable Double Opt-In
                    </Button>
                </div>
            </NiceModal>
        </>
    );
}
